import type { GetOrganizationsInGeoAreaTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { AccountBalanceIcon, LocationIcon } from '@/assets';
import { useGeoAreaNameMap } from '@/features/api/hooks/shared-app/useGeoAreaNameMap';
import { useGetGeoAreaTree } from '@/features/api/hooks/shared-app/useGetGeoAreaTree';
import { useBottomModal } from '@/features/popup/provider/useBottomModal';
import { FilterChip } from '@/features/profile-setting/components/organization-area-filters/FilterChip';
import { TagPickerContent } from '@/features/profile-setting/components/organization-area-filters/TagPickerContent';
import { useOrganizationFilterLabels } from '@/features/profile-setting/components/organization-area-filters/useOrganizationFilterLabels';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { GeoAreaSelectionModal } from '@/shared/components/forms/GeoAreaSelectionModal';
import { useGeoAreaSelection } from '@/shared/components/forms/useGeoAreaSelection';

type TagValue = GetOrganizationsInGeoAreaTagsEnum;

type Props = {
  availableTags: TagValue[];
  selectedTags: TagValue[];
  onChangeTags: (tags: TagValue[]) => void;
  organizationId: string;
  selectedGeoAreaIds: string[];
  onChangeGeoAreaIds: (ids: string[]) => void;
};

export const OrganizationAreaFilters: FC<Props> = ({
  availableTags,
  selectedTags,
  onChangeTags,
  organizationId,
  selectedGeoAreaIds,
  onChangeGeoAreaIds
}) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-area.filters'
  });

  const { showBottomModal, clearAllBottomModals } = useBottomModal();
  const geoAreaNameById = useGeoAreaNameMap(organizationId);
  const { data: geoAreaTree, isLoading } = useGetGeoAreaTree(organizationId);
  const geoAreaSelection = useGeoAreaSelection(geoAreaTree);

  const { geoAreaLabel, tagsLabel } = useOrganizationFilterLabels({
    selectedTags,
    selectedGeoAreaIds,
    geoAreaNameById
  });

  const handleOpenGeoAreaPicker = useCallback(() => {
    geoAreaSelection.handleOpen(selectedGeoAreaIds);
  }, [geoAreaSelection, selectedGeoAreaIds]);

  const handleOpenTagPicker = useCallback(() => {
    showBottomModal({
      title: t('tagsSheetTitle'),
      children: (
        <TagPickerContent
          availableTags={availableTags}
          initialSelectedTags={selectedTags}
          onConfirm={(tags) => {
            onChangeTags(tags);
            clearAllBottomModals();
          }}
        />
      )
    });
  }, [
    availableTags,
    clearAllBottomModals,
    onChangeTags,
    selectedTags,
    showBottomModal,
    t
  ]);

  return (
    <>
      <View style={styles.container}>
        <FilterChip
          icon={LocationIcon}
          label={geoAreaLabel}
          active={selectedGeoAreaIds.length > 0}
          onPress={handleOpenGeoAreaPicker}
        />
        <FilterChip
          icon={AccountBalanceIcon}
          label={tagsLabel}
          active={selectedTags.length > 0}
          onPress={handleOpenTagPicker}
        />
      </View>
      <GeoAreaSelectionModal
        selection={geoAreaSelection}
        title={t('geoAreaSheetTitle')}
        isLoading={isLoading}
        onDone={() => geoAreaSelection.handleDone(onChangeGeoAreaIds)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingBottom: 32,
    alignItems: 'flex-start',
    gap: 8
  }
});
